import { csrfToken } from "./csrfTokenUtils";
import { viewerSessionHeaders } from "./viewerSession";

const normalizeUrl = (url) => (url?.startsWith("/") ? url : `/${url}`);

// Resolves once the user has re-authenticated after a session timeout.
const waitForSessionRestore = () =>
    new Promise((resolve) => {
        const handler = () => {
            window.removeEventListener("app:session-restored", handler);
            resolve();
        };
        window.addEventListener("app:session-restored", handler);
    });

const handleUnauthorized = (response) => {
    if (response.status === 401) {
        if (typeof window !== "undefined" && window.__REQUIRES_AUTH__ && !window.__SESSION_TIMEOUT_ACTIVE__) {
            window.__SESSION_TIMEOUT_ACTIVE__ = true;
            window.dispatchEvent(new CustomEvent("app:session-expired"));
        }
        return true;
    }
    return false;
};

// Global request deduplication - prevents duplicate calls across entire app
// Using a synchronous lock to prevent race conditions
const pendingRequests = new Map();

const PENDING_REQUEST_TTL_MS = 30000;

const getPendingRequest = (key) => {
    const entry = pendingRequests.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > PENDING_REQUEST_TTL_MS) {
        pendingRequests.delete(key);
        return null;
    }
    return entry.promise;
};

const setPendingRequest = (key, promise) => {
    pendingRequests.set(key, { promise, timestamp: Date.now() });
};

const apiGETRequest = async (url = null) => {
    if (url == null) return { error: "URL is missing" };

    const normalizedUrl = normalizeUrl(url);

    // CRITICAL: Synchronously check and set to prevent race conditions
    // If there's already a pending (and not stale) request, return it immediately
    const existing = getPendingRequest(normalizedUrl);
    if (existing) {
        return existing;
    }

    // CRITICAL: Create promise FIRST, then store it synchronously
    // This ensures any simultaneous calls will get the same promise
    let resolvePromise;
    let rejectPromise;

    const requestPromise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
    });

    // Store the promise IMMEDIATELY (synchronously) before any async operations
    setPendingRequest(normalizedUrl, requestPromise);

    // Now make the actual fetch request
    (async () => {
        try {
            const response = await fetch(normalizedUrl, {
                method: "GET",
                credentials: "include",
                headers: {
                    "X-CSRF-Token": csrfToken(),
                    "Accept": "application/json",
                    ...viewerSessionHeaders(),
                },
            });

            if (handleUnauthorized(response)) {
                // Hold the promise open until the user re-logs-in, then retry.
                await waitForSessionRestore();
                const retryResponse = await fetch(normalizedUrl, {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "X-CSRF-Token": csrfToken(),
                        "Accept": "application/json",
                    },
                });
                resolvePromise(retryResponse.ok ? await retryResponse.json() : { error: "Retry failed" });
                return;
            }
            const responseJSON = await response.json();
            resolvePromise(responseJSON);
        } catch (error) {
            rejectPromise({ error: error.message });
        } finally {
            // CRITICAL: Remove from pending requests AFTER completion
            // This allows future calls to go through, but prevents simultaneous duplicates
            pendingRequests.delete(normalizedUrl);
        }
    })();

    return requestPromise;
};

export const apiJsonRequest = async (url = null, method = "POST", payload = {}) => {
    if (url == null) return { error: "URL is missing" };

    const normalizedUrl = normalizeUrl(url);
    const upperMethod = (method || "POST").toUpperCase();

    // For POST/PUT/PATCH requests, create a unique key from URL + payload
    // This allows deduplication of identical requests while allowing different payloads
    let requestKey = normalizedUrl;
    if (!["GET", "HEAD"].includes(upperMethod)) {
        // Create a stable key from the payload (excluding authenticity_token which changes)
        const payloadForKey = { ...(payload || {}) };
        // Sort keys for consistent serialization
        const sortedPayload = Object.keys(payloadForKey)
            .sort()
            .reduce((acc, key) => {
                acc[key] = payloadForKey[key];
                return acc;
            }, {});
        const payloadString = JSON.stringify(sortedPayload);
        requestKey = `${normalizedUrl}::${upperMethod}::${payloadString}`;
    }

    // CRITICAL: Check if there's already a pending (and not stale) request with the same key
    const existingRequest = getPendingRequest(requestKey);
    if (existingRequest) {
        return existingRequest;
    }

    // Create the request promise
    const requestPromise = new Promise(async (resolve, reject) => {
        try {
            const payloadWithToken = !["GET", "HEAD"].includes(upperMethod)
                ? { ...(payload || {}), authenticity_token: csrfToken() }
                : {};

            const requestInit = {
                method: upperMethod,
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken(),
                    "Accept": "application/json",
                    ...viewerSessionHeaders(),
                },
            };

            if (!["GET", "HEAD"].includes(upperMethod)) {
                requestInit.body = JSON.stringify(payloadWithToken);
            }

            const response = await fetch(normalizedUrl, requestInit);

            if (handleUnauthorized(response)) {
                // Hold the promise open until the user re-logs-in, then retry.
                await waitForSessionRestore();
                const retryPayload = !["GET", "HEAD"].includes(upperMethod)
                    ? { ...(payload || {}), authenticity_token: csrfToken() }
                    : {};
                const retryInit = {
                    method: upperMethod,
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": csrfToken(),
                        "Accept": "application/json",
                    },
                };
                if (!["GET", "HEAD"].includes(upperMethod)) {
                    retryInit.body = JSON.stringify(retryPayload);
                }
                const retryResponse = await fetch(normalizedUrl, retryInit);
                const data = await retryResponse.json();
                resolve({ data });
                return;
            }

            const data = await response.json();
            resolve({ data });
        } catch (error) {
            reject({ error: error.message || error });
        } finally {
            // Remove from pending requests AFTER completion
            pendingRequests.delete(requestKey);
        }
    });

    // Store the promise IMMEDIATELY (synchronously) before any async operations
    setPendingRequest(requestKey, requestPromise);

    return requestPromise;
};

export default apiGETRequest;
